import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

import { isBrowser } from "./env";
import { lookup } from "./lookup";
import {
  DEFAULT_VARIANT,
  VARIANT_ALIASES,
  VARIANTS,
  type BrandVariant,
  type VariantId,
} from "./tokens";

interface BrandContextValue {
  variant: BrandVariant;
  variantId: VariantId;
  setVariant: (id: VariantId) => void;
}

const BrandContext = createContext<BrandContextValue | null>(null);

const STORAGE_KEY = "vy-brandbook-variant";
const URL_PARAM = "v";

function resolveVariant(raw: string | null): VariantId | null {
  if (!raw) {
    return null;
  }
  if (raw in VARIANTS) {
    // SAFETY: `raw in VARIANTS` proved it is one of the VariantId keys.
    return raw as VariantId;
  }
  return lookup(VARIANT_ALIASES, raw) ?? null;
}

function readInitialVariant(): VariantId {
  if (!isBrowser()) {
    return DEFAULT_VARIANT;
  }

  const url = new URL(window.location.href);
  const fromUrl = resolveVariant(url.searchParams.get(URL_PARAM));
  if (fromUrl) {
    return fromUrl;
  }

  const fromStorage = resolveVariant(window.localStorage.getItem(STORAGE_KEY));
  if (fromStorage) {
    return fromStorage;
  }

  return DEFAULT_VARIANT;
}

function applyTokensToRoot(variant: BrandVariant) {
  const root = document.documentElement;
  root.style.setProperty("--bg", variant.palette.bg);
  root.style.setProperty("--bg-soft", variant.palette.bgSoft);
  root.style.setProperty("--paper", variant.palette.paper);
  root.style.setProperty("--ink", variant.palette.ink);
  root.style.setProperty("--ink-soft", variant.palette.inkSoft);
  root.style.setProperty("--amber", variant.palette.amber);
  root.style.setProperty("--amber-light", variant.palette.amberLight);
  root.style.setProperty("--peach", variant.palette.peach);
  root.style.setProperty("--peach-deep", variant.palette.peachDeep);
  root.style.setProperty("--rule", variant.palette.rule);

  root.style.setProperty("--font-display", variant.fonts.display);
  root.style.setProperty("--font-body", variant.fonts.body);
  root.style.setProperty("--font-flair", variant.fonts.flair);
  root.style.setProperty("--font-devanagari", variant.fonts.devanagari);

  root.style.setProperty("--radius", variant.treatment.radius);
  root.style.setProperty("--shadow", variant.treatment.shadow);
  root.style.setProperty(
    "--display-weight",
    String(variant.treatment.headlineWeight),
  );
  root.style.setProperty(
    "--display-tracking",
    variant.treatment.headlineLetterSpacing,
  );
  root.style.setProperty(
    "--display-lh",
    String(variant.treatment.headlineLineHeight),
  );

  root.style.setProperty(
    "--display-variation",
    variant.fonts.displayVariation ?? "normal",
  );

  root.dataset.variant = variant.id;
}

export function BrandProvider({ children }: { children: ReactNode }) {
  const [variantId, setVariantId] = useState<VariantId>(readInitialVariant);

  const variant = VARIANTS[variantId];

  useEffect(() => {
    applyTokensToRoot(variant);
  }, [variant]);

  const setVariant = useCallback((id: VariantId) => {
    setVariantId(id);
    if (isBrowser()) {
      window.localStorage.setItem(STORAGE_KEY, id);
      const url = new URL(window.location.href);
      url.searchParams.set(URL_PARAM, id);
      window.history.replaceState({}, "", url);
    }
  }, []);

  const value = useMemo<BrandContextValue>(
    () => ({ variant, variantId, setVariant }),
    [variant, variantId, setVariant],
  );

  return (
    <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
  );
}

export function useBrand(): BrandContextValue {
  const ctx = useContext(BrandContext);
  if (!ctx) {
    throw new Error("useBrand must be used within a BrandProvider");
  }
  return ctx;
}
